import { useCallback, useLayoutEffect, useRef, useState } from 'react';

// Only a fallback -- the real gap is read from the probe row's own computed
// style on every measurement (see readGapPx), so it reflects the user's
// actual root font size. The gap is authored in rem (PrimaryNav's
// visibleRowStyle/probeRowStyle), so a hardcoded px constant would silently
// undercount every gap if the browser's default font size changed.
const GAP_PX_FALLBACK = 24;
const SAFETY_BUFFER_PX = 8;

export interface ResponsiveNavOverflowResult {
  /** Attach to the flex item whose shrunk clientWidth is the space actually
   * available for nav items. It's a flex sibling of the real "More" button,
   * so flexbox has already excluded that button's width from this
   * clientWidth -- nothing here needs to reserve space for it again. */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Attach to an off-screen row that unconditionally renders all
   * `itemCount` candidate items, in priority order, with the same gap/font
   * styling as the real row -- only its children's natural widths matter. */
  probeRef: React.RefObject<HTMLDivElement>;
  /** How many of the `itemCount` candidates (from the front) fit. */
  visibleCount: number;
}

function readGapPx(probe: HTMLDivElement): number {
  const parsed = parseFloat(window.getComputedStyle(probe).columnGap);
  return Number.isFinite(parsed) ? parsed : GAP_PX_FALLBACK;
}

/**
 * Priority-nav width fitting: decides how many priority-ordered nav items
 * fit in the available width, so the rest spill into an overflow menu
 * instead of wrapping or clipping. Re-measures whenever the container
 * resizes, or whenever the probe row's own content width changes (e.g. the
 * active tab switching to a bolder/larger font on a `view` change, with no
 * container resize involved).
 *
 * jsdom has no real layout engine and doesn't implement ResizeObserver, so
 * every measured width would be 0 there -- rather than pretend that's a
 * real "nothing fits" answer, this falls back to showing every item, which
 * is also the only sensible default outside a real browser.
 */
export function useResponsiveNavOverflow(
  itemCount: number
): ResponsiveNavOverflowResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(itemCount);

  const recompute = useCallback(() => {
    const container = containerRef.current;
    const probe = probeRef.current;
    if (!container || !probe) return;

    const availableWidth = container.clientWidth;
    const gapPx = readGapPx(probe);
    const itemWidths = Array.from(probe.children).map(
      child => child.getBoundingClientRect().width
    );

    let used = SAFETY_BUFFER_PX;
    let count = 0;
    for (let i = 0; i < itemWidths.length; i += 1) {
      const next = used + gapPx + itemWidths[i];
      if (next > availableWidth) break;
      used = next;
      count += 1;
    }
    setVisibleCount(count);
  }, []);

  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      setVisibleCount(itemCount);
      return undefined;
    }

    recompute();
    const ro = new ResizeObserver(recompute);
    if (containerRef.current) {
      ro.observe(containerRef.current);
    }
    if (probeRef.current) {
      ro.observe(probeRef.current);
    }
    return () => ro.disconnect();
    // itemCount changes the probe row's own children (e.g. isAdmin toggling
    // Product Types/Therapeutic Areas), so it must trigger a re-measure.
  }, [recompute, itemCount]);

  return { containerRef, probeRef, visibleCount };
}
